import { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import CustomModal from 'src/components/modal/customModal';
import { styled, useTheme } from '@mui/material/styles';
import FormProvider, { RHFCheckbox, RHFRadioGroup, RHFSelect, RHFTextField } from 'src/components/hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { axiosGetCall,  axiosPostCall } from 'src/services/apisCall';
import { VOICE } from 'src/lib/Enums';
import { checkButtonAccess } from 'src/utils/common';
import { jwtDecode } from 'jwt-decode';
import Logger from 'src/lib/Logger';
const logger = new Logger('voiceHostAdd');

const actionList = [
  { label: 'Add CLIs', value: 'add' },
  { label: 'Delete CLIs', value: 'delete' }
];

const InputContainer = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: '20px',
}));
const FieldHeading = styled(Typography)(() => ({
  fontWeight: 500,
}));

const splitClis = (value) =>
  String(value || '')
    .split(/[\n,]+/)
    .map((cli) => cli.trim())
    .filter(Boolean);

const getSelectedGateways = (selectedGateways) =>
  Object.entries(selectedGateways || {})
    .filter(([, checked]) => checked)
    .map(([key]) => key.replace('gateway_', ''));

const getGatewayCheckboxValues = (gatewayArr) => {
  const gateways = Array.isArray(gatewayArr)
    ? gatewayArr
    : String(gatewayArr || '')
      .split(',')
      .map((gatewayId) => gatewayId.trim())
      .filter(Boolean);

  return gateways.reduce((acc, gatewayId) => {
    acc[`gateway_${String(gatewayId)}`] = true;
    return acc;
  }, {});
};

const AddCli = ({ openModal, handleCloseModal, title, forceLoad, updateUserId, readdCli }) => {
  const [canSubmit, setCanSubmit] = useState(true);
  const [hostDetails, setHostDetails] = useState(null);
  const [gatewaysArr, setGatewaysArr] = useState([]);
  const [cliReport, setCliReport] = useState(null);

  const { email } = jwtDecode(localStorage.getItem('token'));

  const { CLI_TYPE } = VOICE;
  const theme = useTheme();
  const isReaddMode = Boolean(readdCli?.cli);

  const Schema = Yup.object().shape({
    action: Yup.string().oneOf(['add', 'delete']).required('Action is required!'),
    cliType: Yup.string().required('CLI Type is required!'),
    gatewayCli: Yup.string().trim().required("Gateway Cli's is required!"),
    selectedGateways: Yup.object().when('action', {
      is: 'add',
      then: (schema) => schema.test(
        'one-gateway',
        'Gateway is required!',
        (value) => Object.values(value || {}).some(Boolean)
      ),
      otherwise: (schema) => schema.notRequired(),
    }),
    duplicateClis: Yup.string().when('action', {
      is: (action) => action === 'add' && !updateUserId,
      then: (schema) => schema.required('Duplicate CLI action is required!'),
      otherwise: (schema) => schema.notRequired(),
    }),
    hardUpdateCondition: Yup.string().when('duplicateClis', {
      is: 'hardUpdate',
      then: (schema) => schema.required('Hard Update Condition is required!'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      action: 'add',
      cliType: '',
      gatewayCli: '',
      duplicateClis: '',
      hardUpdateCondition: '',
      selectedGateways: {},
    },
  });

  const { control, watch, setValue, handleSubmit, reset, formState: { errors } } = methods;

  const values = watch();

  // Count total validation errors
  const totalErrors = Object.keys(errors).length;

  const onSubmit = async(data) => {
    setCanSubmit(false);
  try{
    let response;
    const clis = splitClis(data?.gatewayCli);
    let payload = {
      cliType: data?.cliType,
      clis,
      gateways: getSelectedGateways(data?.selectedGateways),
      duplicateClis: data?.duplicateClis,
    };
    if (data?.duplicateClis === "hardUpdate") {
      payload.hardUpdateCondition = data?.hardUpdateCondition
    };
      if (updateUserId) {
        payload.action = "update";
        payload.adminEmail = email;
        response = await axiosPostCall("updateVoiceClis", payload);
    } else if (data?.action === "delete") {
      payload = {
        clis,
        action: "delete",
        gateways: [],
        cliType: data?.cliType,
      };
      response = await axiosPostCall("updateVoiceClis", payload);
    } else {
      response = await axiosPostCall("addVoiceClis", payload);
    }

    setCliReport(data?.action === "add" && !updateUserId && !isReaddMode ? response : null);

    const successMessage = updateUserId
      ? 'CLI updated successfully'
      : isReaddMode
        ? response?.message || 'CLI re-added successfully'
      : data?.action === "delete"
        ? response?.message || 'CLI deleted successfully'
        : 'CLI added successfully';
    renderSuccessMessage(successMessage);
    if ((data?.action === "delete" && !updateUserId) || isReaddMode) {
      forceLoad(true);
      handleCloseModal();
    }
    // handleCloseModal();
    reset();
    forceLoad(true);

  } catch (error) {
    logger.error('Add Gateway [handleSubmit]', error);
    const errorMessage = error?.message;
    renderErrorMessage(errorMessage);
  } finally {
    setCanSubmit(true);
  }
  };

  const onCancel = () => {
    reset ();
  };

useEffect(() => {
  if (updateUserId) {
    const fetchGatewayList = async () => {
      try {
        const { cliList } = await axiosGetCall("getVoiceCliList", { search: updateUserId });
        setHostDetails(cliList?.[0]);
      } catch (error) {
        // logger.error("Gateway List", error);
        renderErrorMessage(error?.message);
      }
    };

    fetchGatewayList();
  } else if (isReaddMode) {
    setHostDetails(null);
    reset({
      action: 'add',
      cliType: Number(readdCli?.cli_type),
      gatewayCli: readdCli?.cli,
      duplicateClis: 'hardUpdate',
      hardUpdateCondition: '',
      selectedGateways: {},
    });
  } else {
    setHostDetails(null);
    reset({
      action: 'add',
      cliType: '',
      gatewayCli: '',
      duplicateClis: '',
      hardUpdateCondition: '',
      selectedGateways: {},
    });
    gatewaysArr.forEach((gateway) => {
      const gatewayId = String(gateway?.gateway_id);
      setValue(`selectedGateways.gateway_${gatewayId}`, false);
    });
  }
}, [updateUserId, readdCli, isReaddMode, reset, gatewaysArr, setValue]);



useEffect(() => {
  if (hostDetails && updateUserId && gatewaysArr.length) {
    const selectedGateways = getGatewayCheckboxValues(hostDetails?.gateway_arr);

    reset({
      action: 'add',
      cliType: Number(hostDetails?.cli_type),
      gatewayCli: hostDetails?.cli,
      selectedGateways,
    });

    Object.entries(selectedGateways).forEach(([gatewayKey, checked]) => {
      setValue(`selectedGateways.${gatewayKey}`, checked);
    });
  }
}, [hostDetails, updateUserId, gatewaysArr, reset, setValue]);

useEffect(() => {
  getGatewaysArr();
}, []);

const getGatewaysArr = async () => {
  try {
    const response = await axiosGetCall('getGatewaysArr');
    setGatewaysArr(response?.arrGateways || []);
    (response?.arrGateways || []).map((gateway) => {
      const gatewayId = String(gateway?.gateway_id);
      setValue(`selectedGateways.gateway_${gatewayId}`, false);
    });
  } catch (error) {
    logger.error('Error fetching gateways array:', error);
    renderErrorMessage(error?.message || 'Something went wrong while fetching gateways array');
  }
}

const handleDownloadCliReport = () => {
  if (!cliReport) return;

  const addedClis = cliReport?.addedClis || [];
  const updatedClis = cliReport?.updatedClis || [];
  const skippedClis = cliReport?.skippedClis || [];

  const maxRows = Math.max(
    addedClis.length,
    updatedClis.length,
    skippedClis.length
  );

  const headers = ["AddedCLIs", "UpdatedCLIs", "SkippedCLIs"];

  const rows = [];

  for (let i = 0; i < maxRows; i++) {
    rows.push([
      addedClis[i] || "",
      updatedClis[i] || "",
      skippedClis[i] || "",
    ]);
  }

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((item) => `"${item}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "cli_report.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};



  const description = (
    <Box>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction="column"
          gap={2}
          sx={{ p: '25px', overflowY: 'auto', maxHeight: '600px', height: '100%', width: '700px' }}
        >
          {!updateUserId && !isReaddMode && (
            <InputContainer>
              <RHFSelect name="action" label="Action" control={control}>
                {actionList.map((ele) => (
                  <MenuItem key={ele.value} value={ele.value}>
                    {ele.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </InputContainer>
          )}
            <InputContainer>
              <RHFSelect
                name="cliType"
                label="CLI Type"
                control={control}
                disabled={isReaddMode}
              >
                {CLI_TYPE.map((ele) => (
                  <MenuItem key={ele.value} value={ele.value}>
                    {ele.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </InputContainer>
            <InputContainer>
            <RHFTextField name="gatewayCli" label="Cli's" placeholder="Enter Cli's." multiline
              minRows={3}
              maxRows={8} 
              disabled={updateUserId || isReaddMode}/>
            </InputContainer>
            {values.action === "add" && !isReaddMode && !updateUserId && (
               <InputContainer>
              <Typography sx={{ mr : 2 }}>Duplicate ClIs</Typography>
              <RHFRadioGroup
                name="duplicateClis"
                row
                spacing={2}
                container={{ display: 'flex', alignItems: 'center' }}
                options={[
                  { value: 'skip', label: 'Skip CLI' },
                  { value: 'hardUpdate', label: 'Hard Update CLI' },
                ]}
              />
            </InputContainer>
            )}
            {values.duplicateClis === "hardUpdate" && values.action === "add" &&(
            <InputContainer>
              <Typography sx={{ mr : 2 }}>Hard Update Condition</Typography>
              <RHFRadioGroup
                name="hardUpdateCondition"
                row
                spacing={2}
                container={{ display: 'flex', alignItems: 'center' }}
                options={[
                  { value: 'keep', label: 'Keep Assigned Users' },
                  { value: 'remove', label: 'Remove Assigned Users' },
                ]}
              />
            </InputContainer>
            )}
            { values.action === "add" &&(
              <InputContainer>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography>Gateways</Typography>
                {gatewaysArr.map((gateway) => {
                  const gatewayId = String(gateway?.gateway_id);

                  return (
                    <RHFCheckbox
                      key={gateway?._id || gatewayId}
                      name={`selectedGateways.gateway_${gatewayId}`}
                      label={`${gatewayId} : ${gateway?.name}`}
                    />
                  );
                })}
              </Box>
            </InputContainer>
            )}

            {cliReport && !updateUserId && values.action === "add" && (
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "underline",
                mt: 1,
              }}
              onClick={handleDownloadCliReport}
            >
              Download CLI Report
            </Typography>
          )}
            
            
        

            {/* {checkButtonAccess("addsmsgateway", "w") && (        */}

          <Box sx={{ display: 'flex', gap: '1rem', mt: 3 }}>
            <Box
              sx={{
                width: '7.625rem',
                backgroundColor: canSubmit ? '#212B36' : 'grey',
                borderRadius: '8px',
                p: '0.562rem',
                height: '2.375rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                pointerEvents: canSubmit ? 'auto' : 'none',
              }}
              onClick={handleSubmit(onSubmit)}
            >
              <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                {updateUserId
                  ? "Update"
                  : isReaddMode
                    ? "Re-add"
                    : values.action === "delete"
                      ? "Delete"
                      : "Submit"}
                </Typography>
            </Box>
            <Box
              sx={{
                width: '7.625rem',
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: '0.562rem',
                height: '2.375rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={onCancel}
            >
              <Typography sx={{ fontWeight: 600, color: '#212B36' }}>Cancel</Typography>
            </Box>
          </Box>
            {/* )} */}

        </Stack>
      </FormProvider>
    </Box>
  );

  return (
    openModal && (
      <CustomModal
        open={openModal}
        title={updateUserId ? "Update Voice CLI" : isReaddMode ? "Re-add Voice CLI" : "Add New Voice CLI"}
        description={description}
        close={handleCloseModal}
        isCloseIcon={true}
      />
    )
  );
};

export default AddCli;
